import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { 
  Wallet, 
  CreditCard, 
  TrendingUp, 
  Download
} from "lucide-react";
import { get } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

function toNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtAmount(v) {
  return toNum(v).toLocaleString() + " XAF";
}

const ESSPayslipsPage = () => {
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);
  const [payslips, setPayslips] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [lines, setLines] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const empRes = await get("/api/employees?limit=500");
        const allEmployees = empRes?.data || [];
        const currentEmp = allEmployees.find((e) => e.work_email === user?.email) || null;
        const empId = currentEmp?.id || currentEmp?.employee_id;
        if (!empId) { setLoaded(true); return; }

        const result = await get(`/api/payslips?employee_id=${empId}`);
        const payslipRows = result?.data || [];

        const mapped = payslipRows.map(r => {
          const from = r.date_from ? new Date(r.date_from).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }) : "—";
          const to = r.date_to ? new Date(r.date_to).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";
          return {
            id: r.id,
            period: `${from} – ${to}`,
            name: r.name || r.number || intl.formatMessage({ id: "ess.payslips.fallbackName" }, { id: r.id }),
            gross: toNum(r.gross_wage || r.basic_wage || r.wage || r.basic),
            net: toNum(r.net_wage || r.net_total || r.net),
            state: r.state,
          };
        });

        setPayslips(mapped);
        if (mapped.length > 0) setSelected(mapped[0]);
      } catch (_) {
        setPayslips([]);
      }
      setLoaded(true);
    };
    load();
  }, [intl, user]);

  const loadLines = async (slip) => {
    try {
      const lineRes = await get(`/api/payslips/${slip.id}/lines`);
      const lineRows = lineRes?.data || [];
      const allLines = (lineRows || []).map(l => ({
        name: l.name || intl.formatMessage({ id: "ess.payslips.lineFallback" }),
        total: toNum(l.total),
      }));
      setLines(allLines);
    } catch (_) {
      setLines([]);
    }
  };

  useEffect(() => {
    if (selected) loadLines(selected);
  }, [selected?.id]);

  if (!loaded) return <PageSkeleton />;

  const latest = selected;
  const earnings = lines.filter(l => l.total >= 0);
  const deductions = lines.filter(l => l.total < 0);
  const totalEarnings = earnings.reduce((s, l) => s + toNum(l.total), 0);
  const totalDeductions = Math.abs(deductions.reduce((s, l) => s + toNum(l.total), 0));
  const netPay = totalEarnings - totalDeductions;

  const ytdGross = payslips.reduce((s, p) => s + toNum(p.gross), 0);
  const ytdTax = payslips.reduce((s, p) => s + Math.max(0, toNum(p.gross) - toNum(p.net)), 0);

  const stats = [
    { labelId: "ess.payslips.grossSalary", value: latest ? fmtAmount(latest.gross) : "—", sub: latest?.period || intl.formatMessage({ id: "ess.common.noData" }), icon: Wallet, color: "text-emerald-600 bg-emerald-100" },
    { labelId: "ess.payslips.netPay", value: latest ? fmtAmount(latest.net) : "—", sub: latest?.period || "", icon: CreditCard, color: "text-green-600 bg-green-100" },
    { labelId: "ess.payslips.earningsYtd", value: ytdGross ? fmtAmount(ytdGross) : "—", sub: intl.formatMessage({ id: "ess.payslips.ytdEstimate" }), icon: TrendingUp, color: "text-blue-600 bg-blue-100" },
    { labelId: "ess.payslips.taxYtd", value: ytdTax ? fmtAmount(ytdTax) : "—", sub: intl.formatMessage({ id: "ess.payslips.ytdEstimate" }), icon: TrendingUp, color: "text-amber-600 bg-amber-100" },
  ];

  const handleDownloadPdf = () => {
    if (!latest) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const rows = lines
      .map(l => `<tr><td>${l.name}</td><td class="r">${fmtAmount(l.total)}</td></tr>`)
      .join("");
    w.document.write(`<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>${latest.name}</title>
<style>
  body { font-family: Arial, sans-serif; color: #111; padding: 32px; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .muted { color: #666; font-size: 12px; margin: 0 0 20px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { text-align: left; padding: 8px; border-bottom: 1px solid #eee; font-size: 13px; }
  .r { text-align: right; }
  thead th { border-bottom: 2px solid #333; }
  tr.total td { font-weight: bold; border-top: 2px solid #333; border-bottom: none; }
</style>
</head>
<body>
  <h1>${latest.name}</h1>
  <p class="muted">Période : ${latest.period}</p>
  <table>
    <thead><tr><th>Libellé</th><th class="r">Montant</th></tr></thead>
    <tbody>
      ${rows}
      <tr class="total"><td>${intl.formatMessage({ id: "ess.payslips.netToPay" })}</td><td class="r">${fmtAmount(netPay)}</td></tr>
    </tbody>
  </table>
</body>
</html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {intl.formatMessage({ id: "ess.payslips.title" })}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {intl.formatMessage({ id: "ess.payslips.subtitle" })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ labelId, value, sub, icon: Icon, color }) => (
          <div key={labelId} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: labelId })}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-400 mt-1">{sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon size={20} className={color.split(' ')[0]} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-all hover:shadow-md">
          {latest ? (
            <>
              <h3 className="text-sm font-semibold text-gray-800 mb-4">{intl.formatMessage({ id: "ess.payslips.latestSlip" }, { period: latest.period })}</h3>
              {lines.length > 0 ? (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">{intl.formatMessage({ id: "ess.payslips.earnings" })}</p>
                    <div className="space-y-2.5">
                      {earnings.map((l, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-600">{l.name}</span>
                          <span className="font-medium text-gray-900">{fmtAmount(l.total)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-100">
                        <span className="text-gray-800">{intl.formatMessage({ id: "ess.payslips.totalEarnings" })}</span>
                        <span className="text-emerald-700">{fmtAmount(totalEarnings)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">{intl.formatMessage({ id: "ess.payslips.deductions" })}</p>
                    <div className="space-y-2.5">
                      {deductions.length > 0 ? deductions.map((l, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-600">{l.name}</span>
                          <span className="font-medium text-gray-900">{fmtAmount(Math.abs(l.total))}</span>
                        </div>
                      )) : (
                        <p className="text-sm text-gray-400">{intl.formatMessage({ id: "ess.payslips.noDeductions" })}</p>
                      )}
                      <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-100">
                        <span className="text-gray-800">{intl.formatMessage({ id: "ess.payslips.totalDeductions" })}</span>
                        <span className="text-rose-600">{fmtAmount(totalDeductions)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-4 text-center">{intl.formatMessage({ id: "ess.payslips.detailsUnavailable" })}</p>
              )}
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500">{intl.formatMessage({ id: "ess.payslips.netToPay" })}</span>
                  <p className="text-2xl font-bold text-emerald-700">{fmtAmount(netPay)}</p>
                </div>
                <button
                  onClick={handleDownloadPdf}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-sm"
                >
                  <Download size={16} /> {intl.formatMessage({ id: "ess.payslips.downloadPdf" })}
                </button>
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-gray-400">{intl.formatMessage({ id: "ess.payslips.empty" })}</div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">{intl.formatMessage({ id: "ess.table.period" })}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.table.period" })}</th>
                  <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.payslips.colState" })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payslips.length === 0 ? (
                  <tr><td colSpan={2} className="px-5 py-12 text-center text-gray-400">{intl.formatMessage({ id: "ess.payslips.noSlips" })}</td></tr>
                ) : (
                  payslips.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelected(row)}
                      className={`cursor-pointer transition-colors ${selected?.id === row.id ? "bg-emerald-50" : "hover:bg-gray-50/50"}`}
                    >
                      <td className="px-5 py-3.5 font-semibold text-gray-900">{row.period}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                          row.state === "done" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-amber-50 text-amber-700 border-amber-100"
                        }`}>
                          {row.state === "done"
                            ? intl.formatMessage({ id: "ess.payslips.statePaid" })
                            : row.state === "draft"
                              ? intl.formatMessage({ id: "ess.payslips.stateDraft" })
                              : row.state}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ESSPayslipsPage;
